/**
 * Phase D2.a — Admissions enquiries CSV mapping.
 *
 * Pure helpers for translating an `AdmissionEnquiry` doc into the
 * canonical CSV row consumed by `buildCsv` / `downloadCsv` from
 * `./csv.ts`. Lives in a separate file from the page component so the
 * mapping can be unit-tested in isolation without spinning up the
 * AdmissionsPage's funnel/board/dialog children.
 *
 * Column order is intentionally fixed: stable column ordering means
 * external tooling (Sheets, Excel pivots, Looker uploads) can lean on
 * the layout without re-mapping after every release. Adding columns is
 * a non-breaking change — append-only.
 *
 * Mirrors the J4.a / J4.b / J4.c roster export shape:
 *   - Stable canonical headers list
 *   - String coercions tolerate `null` / `undefined` cleanly
 *   - Booleans render `yes` / `no` for spreadsheet readability
 *   - Stage labels resolve from a caller-supplied `stage_key → label`
 *     lookup so the CSV uses human-readable values rather than the
 *     raw `prospecting` / `interested` / etc. enum keys.
 */

import type {
  AdmissionEnquiry,
  AdmissionFunnelStage,
  CalendarItem,
  StaffRosterItem,
  WorkloadStaffStats,
} from "@eduviaos/shared";

import type { CsvCellValue } from "./csv";

/**
 * Canonical column headers for the Admissions CSV export.
 *
 * Append-only — adding a column at the end keeps existing
 * Sheets/Excel pivots stable. Reordering or removing columns is a
 * BREAKING change for downstream tooling and requires a forward slice.
 */
export const ADMISSIONS_CSV_COLUMNS: ReadonlyArray<string> = [
  "Student name",
  "Student email",
  "Student phone",
  "Parent name",
  "Parent phone",
  "Stage",
  "Source",
  "Branch ID",
  "Assigned to",
  "Collaborators",
  "Next follow-up at",
  "Next follow-up note",
  "Notes",
  "Created at",
  "Last advanced at",
  "Updated at",
];

/**
 * Resolve a stage_key to its human-readable funnel label. Falls back
 * to the raw key when the funnel config doesn't carry the stage (e.g.
 * a legacy enquiry whose stage was deleted from the funnel after the
 * row was created — possible but rare).
 */
export function resolveStageLabel(
  stageKey: string,
  stageMap: ReadonlyMap<string, AdmissionFunnelStage>,
): string {
  const stage = stageMap.get(stageKey);
  return stage?.label ?? stageKey;
}

/**
 * Map a single `AdmissionEnquiry` to a row of CSV cell values in the
 * order defined by `ADMISSIONS_CSV_COLUMNS`. Returns a tuple-shaped
 * array so callers can pass it straight to `buildCsv`.
 *
 * Cells follow the same null/empty discipline as the J4.* exports —
 * `null`/`undefined` collapses to the empty string; collaborators get
 * a single-cell JSON-ish join (`"id1, id2"`) so the row remains
 * 1-line-1-record per RFC 4180; timestamps are passed through as the
 * raw ISO 8601 string for downstream parsing.
 */
export function mapEnquiryToCsvRow(
  enquiry: AdmissionEnquiry,
  stageMap: ReadonlyMap<string, AdmissionFunnelStage>,
): ReadonlyArray<CsvCellValue> {
  return [
    enquiry.student_name,
    enquiry.student_email ?? "",
    enquiry.student_phone ?? "",
    enquiry.parent_name ?? "",
    enquiry.parent_phone ?? "",
    resolveStageLabel(enquiry.stage_key, stageMap),
    enquiry.source ?? "",
    enquiry.branch_id ?? "",
    enquiry.assigned_to ?? "",
    enquiry.collaborators.length === 0 ? "" : enquiry.collaborators.join(", "),
    enquiry.next_followup_at ?? "",
    enquiry.next_followup_note ?? "",
    enquiry.notes ?? "",
    enquiry.created_at,
    enquiry.last_advanced_at ?? "",
    enquiry.updated_at,
  ];
}

// ── Phase D2.b: Admissions Trash CSV ─────────────────────────────────────────
//
// Trash is the soft-deleted slice of the same `/admissions/enquiries`
// surface, so the canonical 16-column schema is reused verbatim. Two
// compliance-focused columns are appended on the right edge:
//
//   - `Deleted at` — raw ISO 8601 timestamp from the BE (the value
//     drives the on-screen `Deleted` date + the purge countdown).
//   - `Days until auto-purge` — derived integer, matches the on-screen
//     `AUTO-PURGE` column. The IA's most common reason to export the
//     trash is a compliance / audit log, so surfacing the countdown
//     directly in the CSV avoids forcing reviewers to re-derive it from
//     `deleted_at` after download.
//
// Append-only — keeps the D2.a `ADMISSIONS_CSV_COLUMNS` prefix stable
// so a single downstream pivot/macro can consume both files. Reordering
// either array is a BREAKING change for downstream tooling.

/**
 * Compliance retention window for soft-deleted admissions enquiries.
 * Mirrors `eduviaos-docs/trd/13-observability-sla.md` §retention +
 * `AdmissionsTrashPage.PURGE_DAYS`. Single source of truth for the
 * countdown math so the CSV always matches the on-screen "AUTO-PURGE"
 * column to the day.
 */
export const ADMISSIONS_TRASH_PURGE_DAYS = 30;

/**
 * Canonical column headers for the Admissions Trash CSV export.
 *
 * Extends `ADMISSIONS_CSV_COLUMNS` (16 cols) with two trash-specific
 * compliance columns on the right edge. Total: 18 columns.
 */
export const ADMISSIONS_TRASH_CSV_COLUMNS: ReadonlyArray<string> = [
  ...ADMISSIONS_CSV_COLUMNS,
  "Deleted at",
  "Days until auto-purge",
];

/**
 * Days remaining until permanent purge for a soft-deleted enquiry.
 * Returns the full retention window (30) if `deleted_at` is null
 * (defensive — the only way to land in the trash result set is to be
 * soft-deleted, so this branch is unreachable in practice but guards
 * against contract drift). Clamps at 0 (never returns negative — once
 * the BE has purged, the row vanishes from the result set anyway).
 *
 * Pure function of `(deletedAt, now)`; tests can pin `now` to a fixed
 * instant so the math is deterministic regardless of system clock.
 */
export function daysUntilPurge(
  deletedAt: string | null,
  now: Date = new Date(),
  purgeDays: number = ADMISSIONS_TRASH_PURGE_DAYS,
): number {
  if (!deletedAt) return purgeDays;
  const deletedAtMs = new Date(deletedAt).getTime();
  if (!Number.isFinite(deletedAtMs)) return purgeDays;
  const elapsedDays = Math.floor((now.getTime() - deletedAtMs) / 86_400_000);
  return Math.max(0, purgeDays - elapsedDays);
}

/**
 * Map a soft-deleted `AdmissionEnquiry` to a row of CSV cell values in
 * the order defined by `ADMISSIONS_TRASH_CSV_COLUMNS`. Reuses
 * `mapEnquiryToCsvRow` verbatim for the 16-column prefix, then appends
 * the two compliance columns.
 *
 * `now` is injected to keep the days-until-purge math deterministic in
 * tests; the page passes `new Date()` at export time so a single
 * "Download" click produces consistent values across the whole batch.
 */
export function mapTrashEnquiryToCsvRow(
  enquiry: AdmissionEnquiry,
  stageMap: ReadonlyMap<string, AdmissionFunnelStage>,
  now: Date = new Date(),
): ReadonlyArray<CsvCellValue> {
  return [
    ...mapEnquiryToCsvRow(enquiry, stageMap),
    enquiry.deleted_at ?? "",
    daysUntilPurge(enquiry.deleted_at, now),
  ];
}

// ── Phase D2.c: Admissions Workload CSV ──────────────────────────────────────
//
// The Workload page (`/admissions/workload`) is a single-shot report — one
// row per staff member with assigned active enquiries, plus an
// `unassigned_active` global tally. There is no pagination and no filter
// toolbar, so the export is far simpler than D2.a / D2.b: callers pass the
// `WorkloadResponse` they already have in memory and we render the table 1:1.
//
// Column ordering is parity with the on-screen DataTable so a user printing
// the CSV next to the page sees the same six columns in the same order.
// An extra `Workload share %` column is appended on the right edge — derived
// at export time as `active / total_active`, rounded to 1 decimal place. It
// matches the audit question "who's carrying what fraction of the queue?"
// that the stat strip's "Avg per staff" only answers obliquely.
//
// A trailing synthetic `(Unassigned)` row is appended whenever
// `unassigned_active > 0` so the CSV is self-contained for audits — the
// auditor doesn't need to cross-reference the StatCard strip to know the
// unowned-queue gap. The synthetic row uses sentinel strings (`(unassigned)`
// in the email column, blank elsewhere) so it sorts last alphabetically and
// is visually distinct in spreadsheets.

/**
 * Canonical column headers for the Admissions Workload CSV export.
 *
 * Mirrors the on-screen DataTable in `AdmissionsWorkloadPage` (Staff member /
 * Active / Overdue / Today / This week) and appends `Workload share %` for
 * audit pivots. Append-only — adding columns at the right edge keeps
 * downstream Sheets/Excel macros stable.
 */
export const ADMISSIONS_WORKLOAD_CSV_COLUMNS: ReadonlyArray<string> = [
  "Staff name",
  "Staff email",
  "Active enquiries",
  "Overdue follow-ups",
  "Due today",
  "Due this week",
  "Workload share %",
];

/**
 * Compute a staff member's share of the total active workload, expressed as
 * a percentage rounded to 1 decimal place. Returns `0` when `totalActive`
 * is `0` (defensive — also matches what the auditor expects when no one
 * has any active enquiries). Always non-negative; clamps no upper bound
 * because per-staff active counts are summed into `totalActive` so a single
 * row's share is bounded by 100 in practice.
 *
 * Pure function exported for the unit-test surface.
 */
export function workloadSharePercent(active: number, totalActive: number): number {
  if (!Number.isFinite(active) || !Number.isFinite(totalActive)) return 0;
  if (totalActive <= 0) return 0;
  return Math.round((active / totalActive) * 1000) / 10;
}

/**
 * Map a single `WorkloadStaffStats` row to a CSV row in the order defined by
 * `ADMISSIONS_WORKLOAD_CSV_COLUMNS`. `totalActive` is injected so the
 * share-percent column reflects the same denominator across every row of
 * a single export.
 */
export function mapWorkloadStaffToCsvRow(
  staff: WorkloadStaffStats,
  totalActive: number,
): ReadonlyArray<CsvCellValue> {
  return [
    staff.user_name || "",
    staff.user_email,
    staff.active_enquiries,
    staff.overdue_followups,
    staff.due_today,
    staff.due_this_week,
    workloadSharePercent(staff.active_enquiries, totalActive),
  ];
}

/**
 * Build the synthetic trailing row that represents the global
 * `unassigned_active` count. Returns `null` when there are no unassigned
 * enquiries (so the CSV stays tight and doesn't carry a 0-row).
 *
 * The synthetic row uses sentinel strings — `Unassigned queue` in the
 * staff-name column and `(unassigned)` in the email column — so spreadsheet
 * users can filter / pivot on it cleanly. Audit-derived counts (overdue /
 * due today / due this week) are intentionally blank because the BE only
 * carries the total `unassigned_active`; padding them with `0` would imply
 * "we verified the breakdown is empty" which we haven't.
 */
export function buildUnassignedWorkloadRow(
  unassignedActive: number,
  totalActive: number,
): ReadonlyArray<CsvCellValue> | null {
  if (unassignedActive <= 0) return null;
  return [
    "Unassigned queue",
    "(unassigned)",
    unassignedActive,
    "",
    "",
    "",
    workloadSharePercent(unassignedActive, totalActive + unassignedActive),
  ];
}

// ── Phase D2.d: Admissions Calendar CSV ──────────────────────────────────────
//
// The Calendar page (`/admissions/calendar`) is the IA's "morning rounds"
// view — a windowed (7/14/30/60 days) list of every scheduled follow-up
// plus the always-included overdue tail. The export is a single-shot
// transform of the already-cached `CalendarResponse` (matches the D2.c
// Workload pattern; no iterative pagination loop because the BE returns
// the entire window in one call).
//
// The first column is `Bucket` — the same Overdue / Today / Tomorrow /
// This week / Later grouping the on-screen page uses to scan. Exporting
// the bucket directly into the CSV lets reviewers sort/filter the
// downloaded file the same way they read the page (overdue first), without
// re-deriving it from the timestamp client-side.
//
// Owner resolution piggybacks on the same `useStaffRoster()` cache the
// page already hits to render the "Owner" column — we accept a
// `rosterMap` lookup so the CSV uses human-readable names instead of
// opaque `user_id` strings. Falls back to "Unassigned" when
// `assigned_to === null`, and to the raw user_id when the roster lookup
// misses (mirrors the stage fallback pattern in D2.a — covers the rare
// case where the assignee was removed from the roster after taking the
// enquiry).
//
// Stage labels use the same `stageMap: ReadonlyMap<string, AdmissionFunnelStage>`
// resolver as D2.a / D2.b so a single funnel cache populates labels
// across the entire Admissions surface.

/**
 * Canonical column headers for the Admissions Calendar CSV export.
 *
 * Append-only — adding a column at the end keeps existing Sheets/Excel
 * macros stable. The 10-column shape pairs the on-screen calendar
 * grouping (Bucket) with the underlying enquiry payload so a single
 * CSV is enough to triage the day's follow-ups without bouncing back
 * to the app.
 */
export const ADMISSIONS_CALENDAR_CSV_COLUMNS: ReadonlyArray<string> = [
  "Bucket",
  "Next follow-up at",
  "Student name",
  "Parent name",
  "Parent phone",
  "Stage",
  "Owner name",
  "Owner email",
  "Next follow-up note",
  "Enquiry ID",
];

/**
 * Bucket label for a calendar item, derived from `next_followup_at` vs.
 * `now`. Mirrors the on-screen grouping in `AdmissionsCalendarPage` so
 * the CSV's first column matches what the IA scans on the page.
 *
 * "Today" / "Tomorrow" are calendar-day comparisons (not 24-hour
 * windows), matching the page's `bucket()` function in
 * `AdmissionsCalendarPage.tsx`. "This week" covers anything in the
 * next seven days that isn't already classified as today / tomorrow.
 * "Later" is the catch-all for the 7+ day tail.
 *
 * Pure function exported for the unit-test surface.
 */
export function calendarBucketLabel(nextFollowupAt: string, now: Date = new Date()): string {
  const at = new Date(nextFollowupAt);
  const atMs = at.getTime();
  if (!Number.isFinite(atMs)) return "Later";
  if (at < now) return "Overdue";
  const dayMs = 86_400_000;
  if (
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate()
  ) {
    return "Today";
  }
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (
    at.getFullYear() === tomorrow.getFullYear() &&
    at.getMonth() === tomorrow.getMonth() &&
    at.getDate() === tomorrow.getDate()
  ) {
    return "Tomorrow";
  }
  if (atMs - now.getTime() <= dayMs * 7) return "This week";
  return "Later";
}

/**
 * Resolve an `assigned_to` user_id to a (name, email) pair via the
 * roster lookup map. Returns `{ name: "Unassigned", email: "" }` when
 * `assignedTo` is null. Returns `{ name: "", email: "" }` plus the raw
 * user_id surfaced via the name when the roster lookup misses — this
 * preserves the audit trail even when the assignee was removed from
 * the roster mid-flight (the on-screen page falls back to "Unassigned"
 * in that case, but for a CSV the user_id is the more honest signal
 * because it preserves the link to the underlying record).
 */
export function resolveCalendarOwner(
  assignedTo: string | null,
  rosterMap: ReadonlyMap<string, StaffRosterItem>,
): { name: string; email: string } {
  if (assignedTo === null) return { name: "Unassigned", email: "" };
  const member = rosterMap.get(assignedTo);
  if (member) return { name: member.full_name?.trim() || member.email, email: member.email };
  return { name: assignedTo, email: "" };
}

/**
 * Map a single `CalendarItem` to a CSV row in the order defined by
 * `ADMISSIONS_CALENDAR_CSV_COLUMNS`. Pure function — accepts pre-built
 * lookup maps for stages + roster so the caller controls invalidation
 * and the helper stays free of TanStack/network concerns.
 */
export function mapCalendarItemToCsvRow(
  item: CalendarItem,
  ctx: {
    stageMap: ReadonlyMap<string, AdmissionFunnelStage>;
    rosterMap: ReadonlyMap<string, StaffRosterItem>;
    now?: Date;
  },
): ReadonlyArray<CsvCellValue> {
  const now = ctx.now ?? new Date();
  const owner = resolveCalendarOwner(item.assigned_to, ctx.rosterMap);
  return [
    calendarBucketLabel(item.next_followup_at, now),
    item.next_followup_at,
    item.student_name,
    item.parent_name ?? "",
    item.parent_phone ?? "",
    resolveStageLabel(item.stage_key, ctx.stageMap),
    owner.name,
    owner.email,
    item.next_followup_note ?? "",
    item.enquiry_id,
  ];
}
